import { Button, Card, Form, Input, Select, Space, Table, Tag } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationSessionSelect } from '@/pages/education/components/EducationLookupSelects';

type ActivityRow = {
  id: string;
  code: string;
  name: string;
  category: string;
  capacity?: number | null;
  fee_amount?: number | null;
  schedule_label?: string | null;
  session_name?: string;
  active_members?: number;
  waitlisted_members?: number;
  is_active?: number | boolean;
};

export default function ActivitiesListPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.activity.manage') || user?.permissions?.includes('education.admin')
  );
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [category, setCategory] = useState<string | undefined>();
  const [q, setQ] = useState('');

  const list = useQuery({
    queryKey: ['education-activities', sessionId, category, q],
    queryFn: async () => {
      const res = await api.get<ActivityRow[]>('/education/activities', {
        params: { session_id: sessionId, category, q: q || undefined },
      });
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  return (
    <EducationPageChrome
      title={t('activityCatalogue')}
      description={t('activityCatalogueIntro')}
      extra={
        <Space>
          {canManage ? (
            <Button type="primary" onClick={() => navigate('/education/activities/new')}>
              {t('newActivity')}
            </Button>
          ) : null}
          <Button onClick={() => navigate('/education/activities')}>{t('backToActivities')}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Form layout="inline" onFinish={() => list.refetch()}>
            <Form.Item label={t('session')}>
              <EducationSessionSelect
                style={{ minWidth: 200 }}
                value={sessionId}
                onChange={(v) => setSessionId(v || undefined)}
                allowClear
              />
            </Form.Item>
            <Form.Item label={t('category')}>
              <Select
                allowClear
                style={{ minWidth: 140 }}
                value={category}
                onChange={(v) => setCategory(v || undefined)}
                options={[
                  { value: 'club', label: t('activityCategoryClub') },
                  { value: 'sport', label: t('activityCategorySport') },
                  { value: 'arts', label: t('activityCategoryArts') },
                  { value: 'other', label: t('activityCategoryOther') },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('search')}>
              <Input value={q} onChange={(e) => setQ(e.target.value)} allowClear style={{ width: 180 }} />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={list.isFetching}>
              {t('filter')}
            </Button>
          </Form>
        </Card>
        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            dataSource={list.data ?? []}
            pagination={{ pageSize: 25 }}
            columns={[
              { title: t('code'), dataIndex: 'code', width: 110 },
              { title: t('name'), dataIndex: 'name' },
              { title: t('category'), dataIndex: 'category', width: 100 },
              { title: t('session'), dataIndex: 'session_name', width: 140, render: (v) => v || '—' },
              {
                title: t('members'),
                width: 110,
                render: (_: unknown, row: ActivityRow) =>
                  `${Number(row.active_members ?? 0)}${row.capacity != null ? ` / ${row.capacity}` : ''}`,
              },
              {
                title: t('waitlisted'),
                dataIndex: 'waitlisted_members',
                width: 90,
                render: (v) => Number(v ?? 0),
              },
              {
                title: t('fee'),
                dataIndex: 'fee_amount',
                width: 90,
                render: (v) => (v != null ? String(Number(v)) : '—'),
              },
              {
                title: t('status'),
                dataIndex: 'is_active',
                width: 90,
                render: (v) =>
                  v === 0 || v === false ? <Tag>{t('inactive')}</Tag> : <Tag color="success">{t('active')}</Tag>,
              },
              {
                title: t('actions'),
                width: 200,
                render: (_: unknown, row: ActivityRow) => (
                  <Space size="small" wrap>
                    <Button size="small" onClick={() => navigate(`/education/activities/${row.id}/members`)}>
                      {t('members')}
                    </Button>
                    {canManage ? (
                      <Button size="small" onClick={() => navigate(`/education/activities/${row.id}/edit`)}>
                        {t('edit')}
                      </Button>
                    ) : null}
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
